import FButton from 'components/BaseComponents/FButton'
import React, { useCallback, useMemo } from 'react'

import * as Text from 'components/BaseComponents/Text'
import { FormProvider, useForm } from 'react-hook-form'
import { Box } from 'rebass'

import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { input } from 'components/BaseComponents/constants'
import { LBText, LBTextHeader, LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import { StepWizardChildProps } from 'react-step-wizard'
import { useSTheme } from 'styles/theme'
import { ga4_useTitle } from 'utils/ga4'
import XBanner from 'v2/components/XBanner'

import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

interface Props {
    onNextStep?: any
    onPrevious?: any
    householdMembersCount?: any
    householdMembersUnder18Count?: any
    benefitsFlow?: any
    usedForReview?: boolean
    acpOptionOne?: boolean
    personId?: any
    updatePercentage: any
}

const BenefitsIntroduction: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const form = useForm()
    const { handleSubmit } = form

    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)

    ga4_useTitle('SSI household members')

    const suffix = useMemo(() => {
        if (props.usedForReview) {
            return '_review'
        } else {
            return ''
        }
    }, [props.usedForReview])

    const getTranslationKey = (key) => {
        if (t(`${key}${suffix}`) === `${key}${suffix}` || !t(`${key}${suffix}`)) {
            return key
        }
        return `${key}${suffix}`
    }
    const onContinue = async (values) => {
        await props?.nextStep()
    }

    const onPrevious = useCallback(() => {
        props?.onPrevious ? props.onPrevious() : props.previousStep()
        return
    }, [])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.benefits,
        WIZARD_STEPS_MAPPING.benefits.introduction,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    return (
        <FormProvider {...form}>
            <Box as="form" onSubmit={handleSubmit(onContinue)}>
                <FButtonPrevious onClick={props.onPrevious} />

                <LBTextHeader
                    sx={{
                        textAlign: 'left',
                        fontSize: '32px',
                        color: theme.colors.secondaryColor,
                        lineHeight: '120%',
                        fontFamily: 'Raleway',
                        fontWeight: '900',
                    }}
                >
                    {t('ssi_benefits_wizard_household_members_header_review')}
                </LBTextHeader>
                <LBTextSubHeader
                    dangerouslySetInnerHTML={{
                        __html: t('ssi_benefits_wizard_introduction_household_members_sub_header_review'),
                    }}
                />

                <div style={{ marginTop: '20px' }}>
                    <XBanner
                        title={t('benefits_intro_alert1_title')}
                        description={t('benefits_intro_alert1_description')}
                        isButton={false}
                        isCloseButton={true}
                        type="info"
                        marginTop={20}
                    />
                </div>

                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: theme.fonts.longBeach.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                    }}
                >
                    {t('ssi_benefits_wizard_introduction_household_members_paragraph_one')}
                </LBText>

                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: theme.fonts.longBeach.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                    }}
                >
                    {t('ssi_benefits_wizard_introduction_household_members_paragraph_two')}
                </LBText>

                <div style={{ marginTop: '20px' }}>
                    <XBanner
                        title={t('have_more_questions')}
                        description={t('visit_faq')}
                        isButton={true}
                        isCloseButton={true}
                        buttonTitle={t('learn_more')}
                        type="info"
                        isButtonDisabled={false}
                        buttonAction={() => {
                            window.open(
                                'https://www.commoninja.com/faq/lp/e4eded02-fec6-4dc2-b040-31e31fc9b518',
                                '_blank'
                            )
                        }}
                    />
                </div>

                <FButton
                    tabIndex={0}
                    type={'submit'}
                    title={t('save_and_continue')}
                    style={{ marginTop: input.desktop.marginTop }}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('continue')}
                </FButton>
            </Box>
        </FormProvider>
    )
}

export default BenefitsIntroduction
